/**
 * Modal 모듈 (created)
 * @module shared/components/Modal
 * @author Olive
 */

import { useTranslation } from 'react-i18next';
import { ComponentProps, MouseEventHandler, ReactNode } from 'react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPortal,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog.tsx';
import { createRoot } from 'react-dom/client';
import { Icons } from '@/shared/components/Icons.tsx';

/**
 * Modal 종류 (기본: info)
 * @author Olive
 */
export type ModalType = 'information' | 'warning' | 'error' | 'confirm';

/**
 * Modal Interface
 * @author Olive
 */
interface ModelInterface extends ComponentProps<typeof AlertDialogPrimitive.Root> {
  /**
   * Modal 종류
   */
  type: ModalType;
  /**
   * 종료될 때 실행되는 close 함수
   */
  onClose: MouseEventHandler<HTMLButtonElement>;
}

/**
 * Modal 컴포넌트를 생성합니다.
 * @param props - Modal prop
 * @returns Modal
 * @author Olive
 */
export const Modal = ({ type, onClose, ...props }: ModelInterface) => {
  const { t } = useTranslation();

  const iconMap = {
    information: (
      <>
        <Icons.ModalInfo className="mr-2" />
        {t('modal.information')}
      </>
    ),
    warning: (
      <>
        <Icons.ModalError className="mr-2" />
        {t('modal.warning')}
      </>
    ),
    error: (
      <>
        <Icons.ModalError className="mr-2" />
        {t('modal.error')}
      </>
    ),
    confirm: (
      <>
        <Icons.ModalConfirm className="mr-2" />
        {t('modal.confirm')}
      </>
    ),
  };

  return (
    <AlertDialog {...props}>
      <AlertDialogPortal container={document.getElementById('modal-root')!}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center text-[#000] dark:text-[#fff]">
              {iconMap[type]}
            </AlertDialogTitle>
            <AlertDialogDescription>{props.children}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {type === 'confirm' && (
              <AlertDialogCancel className="px-[16.5px]">{t('modal.cancel')}</AlertDialogCancel>
            )}
            <AlertDialogAction
              onClick={onClose}
              className={`px-[16.5px] ${type === 'confirm' ? 'bg-[#4e12ff] text-[#ececec]' : ''}`}
            >
              {type !== 'confirm' ? t('modal.close') : t('modal.continue')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogPortal>
    </AlertDialog>
  );
};

/**
 * 내부 공통 Modal 호출 함수
 * @param type Modal 종류
 * @param children Modal 내용
 * @param onClose 종료 시 실행 함수
 * @returns Modal
 * @author Lian
 */
const showModal = (type: ModalType, children: ReactNode, onClose?: Function): Promise<boolean> => {
  const modalRoot = document.getElementById('modal-root');
  if (!modalRoot) {
    throw new Error('Modal root element (#modal-root) not found');
  }

  const container = document.createElement('div');
  modalRoot.appendChild(container);

  const root = createRoot(container);

  return new Promise(resolve => {
    const cleanup = () => {
      setTimeout(() => {
        root.unmount();
        container.remove();
      }, 100);
    };

    const handleClose = (result: boolean) => () => {
      if (result && typeof onClose === 'function') {
        onClose();
      }
      resolve(result);
      cleanup();
    };

    root.render(
      <Modal
        open
        onOpenChange={open => {
          if (!open) {
            handleClose(false)();
          }
        }}
        type={type}
        onClose={handleClose(true)}
      >
        {children}
      </Modal>,
    );
  });
};

/**
 * 알림창 오픈 함수
 * @param children Modal 내용
 * @param onClose 종료 시 실행 함수
 * @returns
 */
export const ModalInfo = (children: ReactNode, onClose?: Function) =>
  showModal('information', children, onClose);
/**
 * 경고창 오픈 함수
 * @param children Modal 내용
 * @param onClose 종료 시 실행 함수
 * @returns
 */
export const ModalWarn = (children: ReactNode, onClose?: Function) =>
  showModal('warning', children, onClose);
/**
 * 에러창 오픈 함수
 * @param children Modal 내용
 * @param onClose 종료 시 실행 함수
 * @returns
 */
export const ModalError = (children: ReactNode, onClose?: Function) =>
  showModal('error', children, onClose);
/**
 * 확인창 오픈 함수
 * @param children Modal 내용
 * @param onClose 종료 시 실행 함수
 * @returns
 */
export const ModalConfirm = (children: ReactNode, onClose?: Function) =>
  showModal('confirm', children, onClose);
